import { Component, OnDestroy, OnInit, Signal } from '@angular/core';
import { ApplicationAppEntityApplicationRead } from '../../../../../openapi';
import {
  selectApplication,
  selectLoading,
  selectPending
} from '../../../../../store/selectors/application.selectors';
import { Subscription } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { LocalisationForm } from './forms/localisation-form';
import { updateLocalisationData } from '../../../../../store/actions/application.actions';
import { StagesComponent } from '../../../../../common/stages/stages.component';
import { SpinnerComponent } from '../../../../../common/spinner/spinner.component';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ValidationErrorsComponent } from '../../../../../common/validation-errors/validation-errors.component';
import { DatePickerComponent } from '../../../../../common/date-picker/date-picker.component';
import { ParcelsCrudComponent } from '../components/parcels/parcels-crud/parcels-crud.component';
import {
  LocationDecisionsCrudComponent
} from '../components/location-decisions/location-decisions-crud/location-decisions-crud.component';
import {
  ExposedMapControlComponent
} from '../../../../../common/exposed-map/exposed-map-control/exposed-map-control.component';
import {
  ObjectNameOrAdvertisementDescLabelPipe
} from '../../../../../pipes/object-name-or-advertisement-desc-label.pipe';
import { FieldsetParcelsLabelPipe } from '../../../../../pipes/fieldset-parcels-label.pipe';
import { DistanceLabelPipe } from '../../../../../pipes/distance-label.pipe';
import { NgxMaskDirective } from 'ngx-mask';
import { RoadControlsSetComponent } from '../components/road-controls-set/road-controls-set.component';
import { AlertComponent } from '../../../../../common/alert/alert.component';

export interface LocalisationDataComponentConfig {
  stages: string[];
  successUrl: string;
  cancelUrl: string;
  houseNumberControlRequired?: boolean;
  addTaskNameControl?: boolean;
  addLocationDecisionsControl?: boolean;
  addSurfaceTypeControl?: boolean;
  addOpinionOnTheMatterDescControl?: boolean;
  addObjectNameOrDeviceControl?: boolean;
  addObjectOrAdvertisementDescControl?: boolean;
  addDateControls?: boolean;
  addConstructionTechnologyDescriptionControl?: boolean;
  addLeasePurposeControl?: boolean;
  addDistanceControl?: boolean;
  addAreaControls?: boolean;
  addRoadCategoryControl?: boolean;
}

@Component({
  selector: 'app-localisation-data-section',
  standalone: true,
  imports: [
    StagesComponent,
    SpinnerComponent,
    ReactiveFormsModule,
    CommonModule,
    ValidationErrorsComponent,
    DatePickerComponent,
    ParcelsCrudComponent,
    LocationDecisionsCrudComponent,
    ExposedMapControlComponent,
    ObjectNameOrAdvertisementDescLabelPipe,
    FieldsetParcelsLabelPipe,
    DistanceLabelPipe,
    NgxMaskDirective,
    RoadControlsSetComponent,
    AlertComponent
  ],
  providers: [LocalisationForm],
  templateUrl: './localisation-data-section.component.html'
})
export class LocalisationDataSectionComponent implements OnInit, OnDestroy {

  public successUrl: string | undefined = this.route.snapshot.data['successUrl'];

  public cancelUrl: string | undefined = this.route.snapshot.data['cancelUrl'];

  public stages: string[] = this.route.snapshot.data['stages'];

  public application: Signal<ApplicationAppEntityApplicationRead | null> = this.store$.selectSignal(selectApplication);

  public pending: Signal<boolean> = this.store$.selectSignal(selectPending);

  public loading: Signal<boolean> = this.store$.selectSignal(selectLoading);

  private fetchApplication: Subscription = toObservable(this.application).subscribe((app) => {
    if (app) {
      this.form.patchFormValues(app);
    }
  });

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private store$: Store,
    public form: LocalisationForm
  ) {
  }

  public submitAndGoNext(): void {
    const currentApp = this.application();
    if (this.form.valid && currentApp) {
      this.store$.dispatch(updateLocalisationData({
        currentApp,
        payload: this.form.toWriteModel(),
        parcels: this.form.parcelsValues,
        locationDecisions: this.form.locationDecisionsValues,
        successFnc: (applicationId: string) => {
          this.router.navigate(
            [this.successUrl],
            {
              relativeTo: this.route
            }
          );
        },
        failureFnc: () => {
        }
      }));
    } else {
      this.form.markAllAsTouched();
    }
  }

  public onCancel(): void {
    this.router.navigate([this.cancelUrl], { relativeTo: this.route });
  }

  public ngOnInit(): void {
    if (this.route.snapshot.data['addTaskNameControl']) {
      this.form.createTaskNameControl();
    }
    if (!!this.route.snapshot.data['addLocationDecisionsControl']) {
      this.form.createLocationDecisionsControl();
    }
    if (!!this.route.snapshot.data['addSurfaceTypeControl']) {
      this.form.createSurfaceTypeControl();
    }
    if (!!this.route.snapshot.data['addOpinionOnTheMatterDescControl']) {
      this.form.createOpinionOnTheMatterDescControl();
    }
    if (!!this.route.snapshot.data['addObjectNameOrDeviceControl']) {
      this.form.createObjectNameOrDeviceControl();
    }
    if (!!this.route.snapshot.data['addObjectOrAdvertisementDescControl']) {
      this.form.createObjectOrAdvertisementDescControl();
    }
    if (!!this.route.snapshot.data['addDateControls']) {
      this.form.createDateControls();
    }
    if (!!this.route.snapshot.data['addConstructionTechnologyDescriptionControl']) {
      this.form.createConstructionTechnologyDescription();
    }
    if (!!this.route.snapshot.data['addLeasePurposeControl']) {
      this.form.createLeasePurposeControl();
    }
    if (!!this.route.snapshot.data['addDistanceControl']) {
      this.form.createDistanceControl();
    }
    if (!!this.route.snapshot.data['addAreaControls']) {
      this.form.createAreaControls();
    }
    if (!!this.route.snapshot.data['addRoadCategoryControl']) {
      this.form.createRoadCategoryControl();
    }
  }

  public ngOnDestroy(): void {
    this.fetchApplication.unsubscribe();
  }

  readonly Validators = Validators;
}
